import { CircularProgress, SectionSubtitle, SVGIcon } from "@/components";
import { EvaluationData } from "@/components/RaportHSP";
import { EstimationCard } from "@/components/RaportHSP/RaportHSPEstimation";
import { APP_COLORS } from "@/config/colors";
import { formatPrice } from "@/helpers/helpers";
import {
  BarChart,
  DataTable,
} from "@/components";
import clsx from "clsx";

export interface HSPEstimationProps {
  evaluation: EvaluationData;
}

export const HSPEstimation: React.FC<HSPEstimationProps> = ({ evaluation }) => {
  return (
    <div className="mt-6 lg:mt-10">
      <SectionSubtitle
        title="Aktualna wartość rynkowa"
      />
      <div
        className={clsx(
          "mt-11 w-full bg-white border rounded-card border-grayBorder"
        )}
      >
        <div className="flex flex-col h-full lg:flex-row">
          {/* First Column - Current Value */}
          <div className="flex flex-1 gap-[18px] px-6 py-6">
            <div className="bg-primary flex h-11 w-11 items-center justify-center rounded-[10px]">
              <SVGIcon name="price-tag" fill="white" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-inter text-text-primary text-[22px] font-extrabold leading-5">
                {formatPrice(evaluation.currentMarketValue)}
                &nbsp;{evaluation.currency}
              </div>
              <div className="font-inter text-grayText text-base font-normal leading-[18px]">
                aktualna na dzień{" "}
                {evaluation.evaluationDate.split("T")[0]}
              </div>
            </div>
          </div>

          {/* First Divider */}
          <div className="w-full h-px bg-grayBorder lg:block lg:w-px"></div>

          {/* Second Column - New Vehicle Value */}
          <div className="flex flex-1 gap-[14px] px-6 py-6">
            <div className="bg-primary-100 flex h-9 w-9 items-center justify-center rounded-[10px]">
              <SVGIcon name="warning-in-figure" fill="primary" size={20} />
            </div>
            <div className="flex flex-col">
              <div className="font-inter text-grayText text-sm font-normal leading-[16px]">
                Wartość nowego pojazdu
              </div>
              <div className="mt-1 text-base font-extrabold leading-5 font-inter text-text-primary">
                {formatPrice(evaluation.newCarValueWithEquipment)}&nbsp;
                {evaluation.currency}
              </div>
            </div>
          </div>

          {/* Second Divider */}
          <div className="w-full h-px bg-grayBorder lg:block lg:w-px"></div>

          {/* Third Column - Value Loss */}
          <div className="flex relative flex-col flex-wrap flex-1 gap-4 items-start px-6 py-6 md:flex-row">
            {/* Left side - Icon and info */}
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-[14px]">
                <div className="bg-primary-100 flex h-9 w-9 items-center justify-center rounded-[10px]">
                  <SVGIcon name="chart-arrow" fill="primary" />
                </div>
                <div className="flex flex-col">
                  <div className="font-inter text-grayText text-sm font-normal leading-[14px]">
                    Utrata wartości
                  </div>
                  <div className="mt-1 text-base font-extrabold leading-5 font-inter text-text-primary">
                    {formatPrice(evaluation.depreciation.value)}&nbsp;
                    {evaluation.currency}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="font-inter text-grayText mt-6 max-w-[273px] text-[13px] font-normal leading-[18px]">
                Utrata wartości pojazdu jest różnicą w obecnej wycenie pojazdu w stosunku do jego wartości w momencie zakupu.
              </div>
            </div>

            {/* Right side - Circular progress and percentage */}
            <div className="flex flex-col items-center mx-auto mt-2 lg:right-6 lg:top-6">
              <div className="relative">
                <CircularProgress
                  percentage={evaluation.depreciation.rate}
                  size={140}
                  strokeWidth={3}
                  color={APP_COLORS.primary.DEFAULT}
                  backgroundColor="#ECF1F9"
                />
                {/* Percentage in center */}
                <div className="flex absolute inset-0 flex-col justify-center items-center">
                  <div className="font-inter text-text-primary">
                    <span className="text-[26px] font-extrabold">
                      {evaluation.depreciation.rate}
                    </span>
                    <span className="text-base font-extrabold"> %</span>
                  </div>
                  <div className="font-inter text-grayText mt-1 text-[11px] font-normal">
                    Utrata wartości
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="font-inter text-text-primary mb-6 mt-10 text-[16px] font-bold leading-[1.27]">
        Czynniki wpływające na wartość pojazdu
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <EstimationCard
          percentage={`${evaluation.equipmentImpact.rate}%`}
          variant="green"
          title="Wyposażenie dodatkowe"
          price={`+${formatPrice(evaluation.equipmentImpact.value)} ${evaluation.currency
            }`}
          description=""
        />
        <EstimationCard
          percentage={`${evaluation.depreciation.rate}%`}
          variant="red"
          title="Utrata wartości"
          price={`${formatPrice(evaluation.depreciation.value)} ${evaluation.currency
            }`}
          description=""
        />
      </div>

      <div className="pt-8 mt-16">
        <SectionSubtitle
          title="Zmiana wartości pojazdu w czasie"
        />
        <div className="flex flex-row flex-wrap gap-6 mt-16 w-full">
          <div className="flex-1 w-1/2">
            <BarChart
              data={[
                {
                  label: "",
                  value: evaluation.newCarValueWithEquipment,
                  color: APP_COLORS.primary.DEFAULT,
                  topLabel: formatPrice(evaluation.newCarValueWithEquipment),
                },
                {
                  label: new Date(evaluation.evaluationDate)
                    .getFullYear()
                    .toString(),
                  value: evaluation.currentMarketValue,
                  color: APP_COLORS.primary.DEFAULT,
                  topLabel: formatPrice(evaluation.currentMarketValue),
                },
              ]}
              height={500}
              showGrid={true}
              showValues={true}
              showTopLabels={true}
              valueFormat="PLN"
              responsive={true}
              showLegendBelow={false}
            />
          </div>

          <div className="flex-1 w-1/2">
            <DataTable
              data={[
                {
                  id: 1,
                  label: "Cena przy zakupie",
                  value: `${formatPrice(evaluation.newCarValueWithEquipment)} ${evaluation.currency
                    }`,
                },
                {
                  id: 2,
                  label: "Cena bieżąca",
                  value: `${formatPrice(evaluation.currentMarketValue)} ${evaluation.currency
                    }`,
                },
                {
                  id: 3,
                  label: "Utrata wartości",
                  value: `${formatPrice(evaluation.depreciation.value)} ${evaluation.currency
                    }`,
                },
                {
                  id: 4,
                  label: "Procentowa utrata wartości",
                  value: `${evaluation.depreciation.rate}%`,
                },
              ]}
              className="mb-6"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
